import { useEffect, useRef, useState, type ReactNode } from "react";
import { htmlToMd, mdToHtml } from "../lib/md";
import { IcoTrash } from "./icons";
import { EmojiPickerPopover } from "./EmojiPickerPopover";
import { GrammarReviewDialog } from "./GrammarReviewDialog";
import { aiApi } from "../api/endpoints";
import { ApiError } from "../api/client";

type Mode = "wysiwyg" | "md";

interface MdEditorProps {
  /** Markdown source. Read once on mount — subsequent prop changes are ignored. */
  value: string;
  /** Called whenever the editor content changes (MD string). */
  onChange: (md: string) => void;
  /** Show the image-insert button in the toolbar. Default: false. */
  showImageButton?: boolean;
  /** Minimum height of the editor scroll area (WYSIWYG or textarea). Default: 240. */
  minHeight?: number | string;
  /** Initial mode. Default: "wysiwyg". */
  initialMode?: Mode;
  /** Extra content rendered on the right side of the toolbar (e.g. a Delete button). */
  toolbarExtra?: ReactNode;
  /** Extra className on the outer wrapper. */
  className?: string;
  /** Allow distraction-free fullscreen editing. Default: false. */
  enableFocusMode?: boolean;
  /** Document label shown unobtrusively in focus mode. */
  focusTitle?: string;
}

export function MdEditor({
  value,
  onChange,
  showImageButton = false,
  minHeight = 240,
  initialMode = "wysiwyg",
  toolbarExtra,
  className,
  enableFocusMode = false,
  focusTitle,
}: MdEditorProps) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [mdSource, setMdSource] = useState<string>(value);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const mdTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const emojiBtnRef = useRef<HTMLButtonElement | null>(null);
  const initializedRef = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);
  const savedTextareaCursorRef = useRef<{ start: number; end: number } | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [selectionToolbar, setSelectionToolbar] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const captureSelection = () => {
    if (mode === "wysiwyg") {
      const sel = window.getSelection();
      const ed = editorRef.current;
      if (sel && sel.rangeCount > 0 && ed) {
        const r = sel.getRangeAt(0);
        if (ed.contains(r.startContainer)) {
          savedRangeRef.current = r.cloneRange();
        }
      }
    } else {
      const ta = mdTextareaRef.current;
      if (ta) {
        savedTextareaCursorRef.current = {
          start: ta.selectionStart ?? ta.value.length,
          end: ta.selectionEnd ?? ta.value.length,
        };
      }
    }
  };
  const [grammarBusy, setGrammarBusy] = useState(false);
  const [grammarError, setGrammarError] = useState<string | null>(null);
  const [grammarReview, setGrammarReview] = useState<{
    original: string;
    corrected: string;
  } | null>(null);

  // Mount the incoming MD into the contentEditable once. Subsequent `value` prop
  // changes are ignored — the editor owns its state after mount, otherwise every
  // onChange → parent-setState → new value would stomp the caret.
  useEffect(() => {
    if (initializedRef.current) return;
    if (mode === "wysiwyg" && editorRef.current) {
      editorRef.current.innerHTML = mdToHtml(value) as string;
    }
    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When entering wysiwyg from md mode, render the current MD source into the editor.
  useEffect(() => {
    if (!initializedRef.current) return;
    if (mode === "wysiwyg" && editorRef.current) {
      editorRef.current.innerHTML = mdToHtml(mdSource) as string;
    }
    // mdSource is intentionally NOT a dep — we re-render only on mode switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (!focusMode) {
      setSelectionToolbar(null);
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setFocusMode(false);
    };

    const updateSelectionToolbar = () => {
      const selection = window.getSelection();
      const editor = editorRef.current;
      if (!selection || !editor || selection.rangeCount === 0 || selection.isCollapsed) {
        setSelectionToolbar(null);
        return;
      }
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        setSelectionToolbar(null);
        return;
      }
      savedRangeRef.current = range.cloneRange();
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setSelectionToolbar(null);
        return;
      }
      setSelectionToolbar({
        left: Math.min(Math.max(rect.left + rect.width / 2, 118), window.innerWidth - 118),
        top: Math.max(rect.top - 10, 54),
      });
    };

    window.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("selectionchange", updateSelectionToolbar);
    window.addEventListener("resize", updateSelectionToolbar);
    window.addEventListener("scroll", updateSelectionToolbar, true);
    requestAnimationFrame(() => editorRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("selectionchange", updateSelectionToolbar);
      window.removeEventListener("resize", updateSelectionToolbar);
      window.removeEventListener("scroll", updateSelectionToolbar, true);
    };
  }, [focusMode]);

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    if (mode === "wysiwyg" && editorRef.current) {
      const md = htmlToMd(editorRef.current.innerHTML) as string;
      setMdSource(md);
      onChange(md);
    }
    setMode(next);
  };

  const emitFromEditor = () => {
    if (!editorRef.current) return;
    const md = htmlToMd(editorRef.current.innerHTML) as string;
    setMdSource(md);
    onChange(md);
  };

  const exec = (cmd: string, val?: string) => {
    if (mode !== "wysiwyg") return;
    const editor = editorRef.current;
    const selection = window.getSelection();
    const savedRange = savedRangeRef.current;
    if (focusMode && editor && selection && savedRange) {
      try {
        selection.removeAllRanges();
        selection.addRange(savedRange);
      } catch {
        savedRangeRef.current = null;
      }
    }
    editor?.focus();
    document.execCommand(cmd, false, val);
    emitFromEditor();
  };

  const formatBlock = (tag: string) => exec("formatBlock", `<${tag}>`);

  const insertLink = () => {
    const href = prompt("URL:", "https://");
    if (!href) return;
    exec("createLink", href);
  };

  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const currentMarkdown = (): string => {
    if (mode === "wysiwyg" && editorRef.current) {
      return htmlToMd(editorRef.current.innerHTML) as string;
    }
    return mdSource;
  };

  const applyGrammarCorrection = (corrected: string) => {
    setMdSource(corrected);
    if (mode === "wysiwyg" && editorRef.current) {
      editorRef.current.innerHTML = mdToHtml(corrected) as string;
    }
    onChange(corrected);
    setGrammarReview(null);
  };

  const runGrammarCheck = async () => {
    if (grammarBusy) return;
    const text = currentMarkdown().trim();
    if (!text) {
      setGrammarError("Сначала введите текст");
      setTimeout(() => setGrammarError(null), 3000);
      return;
    }
    setGrammarBusy(true);
    setGrammarError(null);
    try {
      const { corrected } = await aiApi.grammarCheck(text, "md");
      setGrammarReview({ original: text, corrected });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Не удалось проверить";
      setGrammarError(msg);
      setTimeout(() => setGrammarError(null), 5000);
    } finally {
      setGrammarBusy(false);
    }
  };

  const insertEmoji = (emoji: string) => {
    if (mode === "wysiwyg" && editorRef.current) {
      const ed = editorRef.current;
      ed.focus();
      const saved = savedRangeRef.current;
      const sel = window.getSelection();
      if (saved && sel) {
        sel.removeAllRanges();
        sel.addRange(saved);
      } else if (sel) {
        // No prior selection — drop caret at the end of the editor.
        const r = document.createRange();
        r.selectNodeContents(ed);
        r.collapse(false);
        sel.removeAllRanges();
        sel.addRange(r);
      }
      const ok = document.execCommand("insertText", false, emoji);
      if (!ok) {
        const sel2 = window.getSelection();
        if (sel2 && sel2.rangeCount > 0) {
          const range = sel2.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(emoji));
          range.collapse(false);
        } else {
          ed.appendChild(document.createTextNode(emoji));
        }
      }
      const sel3 = window.getSelection();
      if (sel3 && sel3.rangeCount > 0) {
        savedRangeRef.current = sel3.getRangeAt(0).cloneRange();
      }
      emitFromEditor();
    } else {
      const ta = mdTextareaRef.current;
      const saved = savedTextareaCursorRef.current;
      if (ta) {
        const start = saved?.start ?? ta.selectionStart ?? ta.value.length;
        const end = saved?.end ?? ta.selectionEnd ?? ta.value.length;
        const next = ta.value.slice(0, start) + emoji + ta.value.slice(end);
        setMdSource(next);
        onChange(next);
        const pos = start + emoji.length;
        savedTextareaCursorRef.current = { start: pos, end: pos };
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(pos, pos);
        });
      } else {
        setMdSource((prev) => {
          const next = prev + emoji;
          onChange(next);
          return next;
        });
      }
    }
    setEmojiOpen(false);
  };

  const onImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result;
      if (typeof result !== "string") return;
      if (mode === "wysiwyg") {
        exec("insertImage", result);
      } else {
        setMdSource((prev) => {
          const next = prev + `\n\n![${file.name}](${result})\n\n`;
          onChange(next);
          return next;
        });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const enterFocusMode = () => {
    if (mode !== "wysiwyg") switchMode("wysiwyg");
    setEmojiOpen(false);
    setFocusMode(true);
  };

  return (
    <div
      className={
        "md-editor" +
        (className ? " " + className : "") +
        (focusMode ? " md-editor--focus" : "")
      }
    >
      {focusMode && (
        <div className="md-focus-head">
          <span className="md-focus-title">{focusTitle?.trim() || "Текст"}</span>
          <span className="md-focus-hint">Esc - выйти</span>
          <button
            type="button"
            className="md-focus-exit"
            onClick={() => setFocusMode(false)}
            aria-label="Выйти из режима фокуса"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6" />
            </svg>
          </button>
        </div>
      )}
      <div className="md-editor-head">
        <div className="landing-toolbar md-editor-toolbar">
          <button
            type="button"
            className="lt-btn"
            title="Заголовок 1"
            onClick={() => formatBlock("h1")}
            disabled={mode !== "wysiwyg"}
          >
            H1
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Заголовок 2"
            onClick={() => formatBlock("h2")}
            disabled={mode !== "wysiwyg"}
          >
            H2
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Заголовок 3"
            onClick={() => formatBlock("h3")}
            disabled={mode !== "wysiwyg"}
          >
            H3
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Абзац"
            onClick={() => formatBlock("p")}
            disabled={mode !== "wysiwyg"}
          >
            ¶
          </button>
          <div className="lt-sep" />
          <button
            type="button"
            className="lt-btn lt-btn-b"
            title="Жирный (Cmd+B)"
            onClick={() => exec("bold")}
            disabled={mode !== "wysiwyg"}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            className="lt-btn lt-btn-i"
            title="Курсив (Cmd+I)"
            onClick={() => exec("italic")}
            disabled={mode !== "wysiwyg"}
          >
            <i>I</i>
          </button>
          <div className="lt-sep" />
          <button
            type="button"
            className="lt-btn"
            title="Маркированный список"
            onClick={() => exec("insertUnorderedList")}
            disabled={mode !== "wysiwyg"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <circle cx="3.5" cy="6" r="0.8" />
              <circle cx="3.5" cy="12" r="0.8" />
              <circle cx="3.5" cy="18" r="0.8" />
            </svg>
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Нумерованный список"
            onClick={() => exec("insertOrderedList")}
            disabled={mode !== "wysiwyg"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="10" y1="6" x2="21" y2="6" />
              <line x1="10" y1="12" x2="21" y2="12" />
              <line x1="10" y1="18" x2="21" y2="18" />
              <path d="M4 6h1v4" />
              <path d="M4 10h2" />
              <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
            </svg>
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Цитата"
            onClick={() => formatBlock("blockquote")}
            disabled={mode !== "wysiwyg"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.75-2-2-2H4c-1.25 0-2 .75-2 2v6c0 1.25.75 2 2 2h3" />
              <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.75-2-2-2h-4c-1.25 0-2 .75-2 2v6c0 1.25.75 2 2 2h3" />
            </svg>
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Разделитель"
            onClick={() => exec("insertHorizontalRule")}
            disabled={mode !== "wysiwyg"}
          >
            —
          </button>
          <div className="lt-sep" />
          <button
            ref={emojiBtnRef}
            type="button"
            className={"lt-btn" + (emojiOpen ? " active" : "")}
            title="Смайлик"
            onMouseDown={(e) => {
              e.preventDefault();
              captureSelection();
            }}
            onClick={() => setEmojiOpen((v) => !v)}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>😀</span>
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Проверить грамматику"
            onClick={runGrammarCheck}
            disabled={grammarBusy}
          >
            {grammarBusy ? (
              <span className="lt-spinner" aria-hidden />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
                <path d="m15 3-9 9 2.5 2.5" opacity="0.35" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="lt-btn"
            title="Ссылка"
            onClick={insertLink}
            disabled={mode !== "wysiwyg"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </button>
          {showImageButton && (
            <button type="button" className="lt-btn" title="Картинка" onClick={insertImage}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>
          )}
          {showImageButton && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onImageFile}
              style={{ display: "none" }}
            />
          )}
          <div className="lt-sep" />
          <div className="md-editor-mode-switch" role="group" aria-label="Режим редактора">
            <button
              type="button"
              className={"lt-btn md-mode-btn" + (mode === "wysiwyg" ? " active" : "")}
              onClick={() => switchMode("wysiwyg")}
              title="Визуальный редактор"
            >
              Визуально
            </button>
            <button
              type="button"
              className={"lt-btn md-mode-btn" + (mode === "md" ? " active" : "")}
              onClick={() => switchMode("md")}
              title="Markdown источник"
            >
              MD
            </button>
          </div>
          {enableFocusMode && (
            <button
              type="button"
              className="lt-btn md-focus-enter"
              title="Режим фокуса"
              aria-label="Режим фокуса"
              onClick={enterFocusMode}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
              </svg>
            </button>
          )}
          {toolbarExtra && <div style={{ flex: 1 }} />}
          {toolbarExtra}
        </div>
      </div>

      <div
        className="md-editor-scroll"
        style={{ minHeight: typeof minHeight === "number" ? `${minHeight}px` : minHeight }}
      >
        {mode === "wysiwyg" ? (
          <div
            ref={editorRef}
            className="md-editor-wysiwyg"
            contentEditable
            suppressContentEditableWarning
            onInput={emitFromEditor}
            onBlur={emitFromEditor}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData("text/plain");
              document.execCommand("insertText", false, text);
              emitFromEditor();
            }}
          />
        ) : (
          <textarea
            ref={mdTextareaRef}
            className="md-editor-md-source"
            value={mdSource}
            onChange={(e) => {
              setMdSource(e.target.value);
              onChange(e.target.value);
            }}
            spellCheck={false}
          />
        )}
      </div>

      {focusMode && selectionToolbar && (
        <div
          className="md-selection-toolbar"
          style={{ left: selectionToolbar.left, top: selectionToolbar.top }}
          role="toolbar"
          aria-label="Форматирование выделенного текста"
          onMouseDown={(e) => e.preventDefault()}
        >
          <button type="button" title="Жирный" aria-label="Жирный" onClick={() => exec("bold")}>
            <b>B</b>
          </button>
          <button type="button" title="Курсив" aria-label="Курсив" onClick={() => exec("italic")}>
            <i>I</i>
          </button>
          <span />
          <button type="button" title="Маркированный список" aria-label="Маркированный список" onClick={() => exec("insertUnorderedList")}>
            •≡
          </button>
          <button type="button" title="Нумерованный список" aria-label="Нумерованный список" onClick={() => exec("insertOrderedList")}>
            1.
          </button>
          <button type="button" title="Ссылка" aria-label="Ссылка" onClick={insertLink}>
            ↗
          </button>
        </div>
      )}

      <EmojiPickerPopover
        open={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        onPick={insertEmoji}
        anchorRef={emojiBtnRef}
      />

      {grammarError && <div className="md-editor-toast md-editor-toast--error">{grammarError}</div>}

      {grammarReview && (
        <GrammarReviewDialog
          original={grammarReview.original}
          corrected={grammarReview.corrected}
          onApply={() => applyGrammarCorrection(grammarReview.corrected)}
          onClose={() => setGrammarReview(null)}
        />
      )}
    </div>
  );
}

// Re-exported for callers that need to pass a branded Delete button into `toolbarExtra`.
export { IcoTrash as MdEditorTrashIcon };
